import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';
import { ACCEPT_INVITE_PATH, useInviteContinuation } from '../hooks/useInviteContinuation';
import { InviteWelcome } from '../components/InviteWelcome';
import { safeContinuePath } from '../utils/safeContinuePath';
import { CHANGE_PASSWORD_PATH } from './ChangePassword';
import { SIGNED_IN_LANDING_PATH, landAfterSignIn } from '../utils/signInLanding';

interface LoginResponse {
  sessionId: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
  hasMultipleWorkspaces: boolean;
  /**
   * A Central admin issued a temporary password. It is replaced before
   * anything else (the server also refuses apps and tokens until it is).
   */
  passwordChangeRequired?: boolean;
  onboardingRequired?: boolean;
  /**
   * The mode the incomplete workspace's owner already chose — resume there.
   * Null/absent when none has been chosen (→ the /onboarding/new chooser).
   */
  onboardingMode?: 'business' | 'personal' | null;
  workspace?: {
    id: string;
    name: string;
  };
}

/**
 * AuthLogin page for OIDC authorization code flow.
 * Handles email/password login and creates a central SSO session.
 * Preserves OAuth params in `continue` query param for flow resumption.
 */
export function AuthLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the continue URL (encoded OAuth params). It ends in
  // `window.location.href = continueUrl` below and now also arrives from
  // emailed verification links, so anything that would leave this origin
  // (https://…, //host, /\host, or their percent-encoded forms) is dropped.
  const continueUrl = safeContinuePath(searchParams.get('continue')) ?? '';
  const registered = searchParams.get('registered') === 'true';
  const authError = searchParams.get('error') || '';

  // Invitation context, when the user arrived here from an invitation. This is
  // the SAME page in every other respect — the context only explains why they
  // are on it and fixes the address the invitation is bound to. Null for every
  // ordinary sign-in, which keeps a free-text email field.
  const inviteContext = useInviteContinuation(continueUrl);
  const invitedEmail = inviteContext?.email ?? '';
  useEffect(() => {
    if (invitedEmail) setEmail(invitedEmail);
  }, [invitedEmail]);

  // Show success toast for newly registered users
  useEffect(() => {
    if (registered) {
      toast.success('Account created successfully! Please sign in.');
    }
  }, [registered]);

  // Surface a friendly message when the Google sign-in session expired
  // (the OAuth callback redirects here with ?error=session_expired when the
  // pending OAuth state is invalid/expired). The user can simply click
  // "Sign in with Google" again.
  useEffect(() => {
    if (authError === 'session_expired') {
      setError('Your Google sign-in session expired. Please try again.');
    }
    if (authError === 'account_deactivated') {
      setError('This account has been deactivated. Contact your administrator.');
    }
  }, [authError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/oidc/session/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Important for cookies
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg =
          data.code === 'ACCOUNT_DEACTIVATED'
            ? 'This account has been deactivated. Contact your administrator.'
            : data.message || 'Invalid email or password';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      const loginData = data as LoginResponse;

      // A temporary password from an admin comes first — before an invitation,
      // the picker, onboarding or the app. The change page resumes `continue`.
      if (loginData.passwordChangeRequired) {
        navigate(
          continueUrl
            ? `${CHANGE_PASSWORD_PATH}?continue=${encodeURIComponent(continueUrl)}`
            : CHANGE_PASSWORD_PATH,
          { replace: true },
        );
        return;
      }

      // AN INVITATION IS A DESTINATION, not a page to be deferred. Neither the
      // workspace picker nor onboarding applies to it: the workspace is the
      // invitation's, and accepting binds the session to it. Either detour
      // would DROP the invitation, leaving it recoverable only by reopening
      // the email — so the invitation is honoured first, and the ordinary
      // branches below are untouched for every other sign-in.
      if (continueUrl.startsWith(ACCEPT_INVITE_PATH)) {
        navigate(continueUrl, { replace: true });
        return;
      }

      // If user has multiple workspaces, redirect to workspace picker
      if (loginData.hasMultipleWorkspaces) {
        toast.success('Welcome back! Choose your workspace.');
        const workspaceUrl = continueUrl
          ? `/auth/workspace?continue=${encodeURIComponent(continueUrl)}`
          : '/auth/workspace';
        navigate(workspaceUrl);
        return;
      }

      // If workspace needs onboarding, redirect to onboarding flow
      if (loginData.onboardingRequired) {
        toast.success('Welcome! Let\'s set up your workspace.');
        // If the user originally entered through /start/{mode}, route them
        // back there so StartEntry can persist onboardingMode='personal' or
        // 'business' before /onboarding/type renders. Jumping straight to
        // /onboarding/type skips that mode-setting step and leaves the
        // workspace with onboardingMode=null — which makes /onboarding/type
        // (and OnboardingLayout's progress bar) render the business default
        // even when the user came in via /start/personal.
        // An explicit onboarding continuation (/start/{mode}, /onboarding/…)
        // is honoured as-is, as the Google callback already does.
        if (continueUrl.startsWith('/start/') || continueUrl.startsWith('/onboarding/')) {
          navigate(continueUrl);
          return;
        }
        // RESUME, DON'T RE-CHOOSE (ONB-02). An owner whose workspace already
        // has a mode resumes it at /start/{mode}, where StartEntry lands them
        // on their recorded step. Sending them to the /onboarding/new chooser
        // instead is how signing in after verifying an email reset progress.
        // The chooser stays for someone who has not picked a mode yet (or
        // owns no workspace).
        const resumeMode =
          loginData.onboardingMode === 'business' || loginData.onboardingMode === 'personal'
            ? loginData.onboardingMode
            : null;
        if (continueUrl.startsWith('/api/')) {
          // Suite-app (OIDC) continuation: it rides along so finishing
          // onboarding hands back to the app. Without a known mode the
          // existing compat route resolves one.
          navigate(
            resumeMode
              ? `/start/${resumeMode}?continue=${encodeURIComponent(continueUrl)}`
              : `/onboarding/type?continue=${encodeURIComponent(continueUrl)}`,
          );
          return;
        }
        // No continuation, or a Central page (/home, …) that needs the
        // finished workspace first.
        navigate(resumeMode ? `/start/${resumeMode}` : '/onboarding/new');
        return;
      }

      // Single workspace with completed onboarding - continue OAuth flow
      toast.success('Welcome back!');
      if (continueUrl) {
        // continueUrl already contains full path (e.g. /api/oidc/authorize?response_type=code&...)
        window.location.href = continueUrl;
      } else {
        // No continue URL: a Central admin lands on the admin console, anyone
        // else on the workspace home (/home), as before. Decided only here,
        // after every continuation above has had its turn.
        await landAfterSignIn(navigate);
      }
    } catch (err) {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        {inviteContext && <InviteWelcome invite={inviteContext} />}

        <div className={inviteContext ? 'mb-6' : 'text-center mb-6'}>
          <h1 className="text-2xl font-semibold text-gray-900">
            {inviteContext ? 'Sign in to accept' : 'Sign in'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {inviteContext
              ? 'Confirm it\'s you and we\'ll add you to the workspace.'
              : 'Enter your credentials to continue'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            {/* LOCKED FOR AN INVITATION, free text for every other sign-in.
                An invitation is bound to one address, so a field the recipient
                can edit here only invites a mismatch the server then has to
                refuse. The value is the invitation's own, derived server-side
                from the token, so editing the DOM changes nothing that
                matters — acceptance re-derives it either way. */}
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus={!invitedEmail}
              readOnly={!!invitedEmail}
              disabled={!!invitedEmail}
              aria-describedby={invitedEmail ? 'invited-email-hint' : undefined}
              className={
                invitedEmail
                  ? 'w-full px-3 py-2 border border-gray-200 rounded-md shadow-sm bg-gray-100 text-gray-600 cursor-not-allowed'
                  : 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500'
              }
              placeholder="you@example.com"
            />
            {invitedEmail && (
              <p id="invited-email-hint" className="mt-1 text-xs text-gray-500">
                This invitation can only be accepted with this address.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Password
              </label>
              <Link
                to={
                  continueUrl
                    ? `/auth/forgot-password?continue=${encodeURIComponent(continueUrl)}`
                    : '/auth/forgot-password'
                }
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-controls="password"
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        {/* Google Sign-in Button */}
        <button
          type="button"
          onClick={() => {
            // Suite-app OIDC flow: continueUrl is the /api/oidc/authorize URL.
            // Append provider=google so the existing OIDC-driven Google flow runs.
            if (continueUrl.startsWith('/api/oidc/authorize')) {
              const separator = continueUrl.includes('?') ? '&' : '?';
              window.location.href = `${continueUrl}${separator}provider=google`;
              return;
            }
            // Otherwise (no continueUrl, or a frontend route like /start/personal):
            // use the backend-initiated session flow. With no continueUrl,
            // return to the sign-in landing, which sends a Central admin to the
            // console and everyone else to /home — never bare `/`, which the
            // callback would turn into /home for everyone. The backend still
            // validates it.
            const returnTo = continueUrl || SIGNED_IN_LANDING_PATH;
            window.location.href = `/api/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`;
          }}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-3 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </button>

        {/* Sign up link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link
              to={
                continueUrl
                  ? `/auth/signup?continue=${encodeURIComponent(continueUrl)}`
                  : '/auth/signup'
              }
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Sign up
            </Link>
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-xs text-amber-800 font-medium mb-2">
              Dev Credentials:
            </p>
            <ul className="text-xs text-amber-700 space-y-1">
              <li>
                <code className="bg-amber-100 px-1 rounded">owner@levelup.dev</code>
              </li>
              <li>
                <code className="bg-amber-100 px-1 rounded">multi@levelup.dev</code> (multi-workspace)
              </li>
              <li>
                Password: your <code className="bg-amber-100 px-1 rounded">DEV_BOOTSTRAP_PASSWORD</code> (backend/.env)
              </li>
            </ul>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
